import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { BulkActionBar } from '@/components/dashboard/BulkActionBar';
import { Star, Search, Eye, ShieldCheck, ShieldX } from 'lucide-react';
import { toast } from 'sonner';
import { logAdminAction } from '@/lib/auditLog';

type Tab = 'pending' | 'published' | 'archived';

export function ReviewsModeration() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('pending');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [detailReview, setDetailReview] = useState<any | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeTargetIds, setRemoveTargetIds] = useState<string[]>([]);
  const [removeReason, setRemoveReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['reviews-moderation', tab],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select('*, profiles!reviews_reviewer_user_id_fkey(full_name, avatar_url)')
        .eq('status', tab)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });

  const filtered = useMemo(() => {
    const list = data ?? [];
    if (!search.trim()) return list;
    const s = search.toLowerCase();
    return list.filter((r: any) =>
      (r.title ?? '').toLowerCase().includes(s) ||
      (r.text ?? '').toLowerCase().includes(s) ||
      (r.profiles?.full_name ?? '').toLowerCase().includes(s),
    );
  }, [data, search]);

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const updateStatus = useMutation({
    mutationFn: async ({ ids, status, reason }: { ids: string[]; status: 'published' | 'archived'; reason?: string }) => {
      const { error } = await supabase
        .from('reviews')
        .update({ status } as any)
        .in('id', ids);
      if (error) throw error;
      await logAdminAction({
        action: status === 'published' ? (ids.length > 1 ? 'bulk_approve' : 'approve') : (ids.length > 1 ? 'bulk_reject' : 'remove'),
        entity_type: 'review',
        metadata: { ids, count: ids.length, reason: reason ?? null },
      });
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['reviews-moderation'] });
      clearSelection();
      toast.success(`${vars.ids.length} review${vars.ids.length === 1 ? '' : 's'} ${vars.status === 'published' ? 'approved' : 'removed'}`);
      setRemoveOpen(false);
      setRemoveReason('');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to update reviews'),
  });

  const openRemoveDialog = (ids: string[]) => {
    setRemoveTargetIds(ids);
    setRemoveReason('');
    setRemoveOpen(true);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Reviews Moderation</h1>
        <p className="text-sm text-muted-foreground">Approve, remove, and triage user-submitted reviews.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 text-sm border-b">
        {(['pending', 'published', 'archived'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); clearSelection(); }}
            className={`pb-2 capitalize transition-colors ${tab === t ? 'text-primary border-b-2 border-primary font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {t === 'archived' ? 'Removed' : t}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by reviewer, title, body..."
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="p-4 md:p-6 space-y-3">
          <BulkActionBar
            selectedCount={selectedIds.size}
            onClear={clearSelection}
            onApprove={() => updateStatus.mutate({ ids: Array.from(selectedIds), status: 'published' })}
            onReject={() => openRemoveDialog(Array.from(selectedIds))}
            pending={updateStatus.isPending}
          />

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">No {tab === 'archived' ? 'removed' : tab} reviews.</p>
          ) : (
            <div className="divide-y">
              {filtered.map((r: any) => {
                const reviewerName = r.profiles?.full_name || 'Anonymous';
                const targetLabel =
                  r.agent_user_id ? 'Agent' :
                  r.property_id ? 'Property' :
                  r.project_id ? 'Project' : '—';
                return (
                  <div key={r.id} className="py-3 flex items-start gap-3 flex-wrap">
                    <Checkbox
                      checked={selectedIds.has(r.id)}
                      onCheckedChange={() => toggleSelected(r.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{reviewerName}</span>
                        <Badge variant="outline" className="text-xs">{targetLabel}</Badge>
                        <span className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className={`h-3.5 w-3.5 ${i < (r.rating ?? 0) ? 'fill-amber-400 text-amber-400' : 'text-muted'}`} />
                          ))}
                        </span>
                        <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                      {r.title && <div className="text-sm font-medium mt-1">{r.title}</div>}
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{r.text || 'No body'}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => setDetailReview(r)}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> View
                      </Button>
                      {r.status !== 'published' && (
                        <Button size="sm" onClick={() => updateStatus.mutate({ ids: [r.id], status: 'published' })} disabled={updateStatus.isPending}>
                          <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Approve
                        </Button>
                      )}
                      {r.status !== 'archived' && (
                        <Button variant="destructive" size="sm" onClick={() => openRemoveDialog([r.id])} disabled={updateStatus.isPending}>
                          <ShieldX className="h-3.5 w-3.5 mr-1" /> Remove
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detailReview} onOpenChange={(v) => !v && setDetailReview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Review details</DialogTitle></DialogHeader>
          {detailReview && (
            <div className="space-y-3 text-sm">
              <div><strong>Reviewer:</strong> {detailReview.profiles?.full_name || 'Anonymous'}</div>
              <div><strong>Rating:</strong> {detailReview.rating} / 5</div>
              {detailReview.title && <div><strong>Title:</strong> {detailReview.title}</div>}
              <div><strong>Body:</strong><br />{detailReview.text || '—'}</div>
              {detailReview.tags?.length > 0 && (
                <div><strong>Tags:</strong> {detailReview.tags.join(', ')}</div>
              )}
              <div className="text-xs text-muted-foreground">Submitted {new Date(detailReview.created_at).toLocaleString()}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove (with reason) dialog */}
      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove {removeTargetIds.length} review{removeTargetIds.length === 1 ? '' : 's'}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">Removed reviews are hidden from public pages. Add an optional reason for the audit log.</p>
            <Textarea
              value={removeReason}
              onChange={(e) => setRemoveReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => updateStatus.mutate({ ids: removeTargetIds, status: 'archived', reason: removeReason || undefined })}
              disabled={updateStatus.isPending}
            >
              <ShieldX className="h-4 w-4 mr-2" /> Confirm remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
